/**
 * Shared Swiper initialiser for COE blocks.
 *
 * LOADING
 * Front end: slider blocks declare "viewScript": [ "coe-swiper-init" ] in
 * block.json, so WordPress loads it only on pages that render one.
 * Editor canvas: viewScript is NOT replayed into the WP 6.3+ iframe —
 * _wp_get_iframed_editor_assets() enqueues editorStyle handles only — so
 * inc/enqueue.php enqueues this handle directly inside its is_admin() branch.
 *
 * CONTRACT
 * Each slider block puts a JSON blob and a stable id on its block wrapper:
 *
 *     data-block-id="block_68b0..."
 *     data-swiper-config='{ ...swiper options..., "coe": { ... } }'
 *
 * Top-level keys go to Swiper untouched. The reserved `coe` object is stripped
 * first and carries this theme's extras:
 *
 *   navVisibility    false | "slides" | "desktop"
 *                    "slides"  hides the nav when the active breakpoint's
 *                              slidesPerView is >= slideCount. This is the rule
 *                              academic-credentials-26 and programs-related-26
 *                              both used inline; it works for fractional
 *                              per-view values, which swiper.isLocked does not.
 *                    "desktop" hides it below the lg breakpoint OR when
 *                              slideCount <= 3 (news-card-slider-26's rule).
 *                    Omit it entirely for the blocks that never managed nav
 *                    visibility — adding it would change their behaviour.
 *   capSlidesPerView true to clamp every whole-number slidesPerView (root and
 *                    each breakpoint) to slideCount, replacing the inline
 *                    Math.min(n, totalSlides) academic-credentials-26 used.
 *   slideCount       integer slide count. Required by both of the above.
 *
 * EXTENSION POINT
 * Blocks with behaviour this file does not own (GA tracking, read-more sizing,
 * range hydration) listen for `coe:swiper:init` on their own wrapper and read
 * event.detail.swiper. Instances are also published at
 * window.coeSwiper.instances[blockId] — the SAME object as
 * window.swiperInstances, which several not-yet-converted blocks still write to.
 */
(function () {
    'use strict';

    var NEXT_SELECTOR = '.slider-next-btn, .swiper-button-next, .swiper-btn-next';
    var PREV_SELECTOR = '.slider-prev-btn, .swiper-button-prev, .swiper-btn-prev';
    var PAGINATION_SELECTOR = '.swiper-pagination';
    var NAV_CONTAINER_SELECTOR = '.slider-navigation';
    var DESKTOP_MIN_WIDTH = 992;

    // Share one registry object. Some blocks convert in later batches and still
    // create window.swiperInstances themselves; two objects would silently
    // diverge, so adopt whichever exists.
    window.swiperInstances = window.swiperInstances || {};
    window.coeSwiper = window.coeSwiper || {};
    window.coeSwiper.instances = window.swiperInstances;

    /**
     * First match inside `root` that belongs to `root` rather than to a nested
     * slider block — a slider inside section-container-26 would otherwise steal
     * its parent's buttons.
     */
    function ownQuery(root, selector) {
        var matches = root.querySelectorAll(selector);
        for (var i = 0; i < matches.length; i++) {
            if (matches[i].closest('[data-swiper-config]') === root) {
                return matches[i];
            }
        }
        return null;
    }

    function parseConfig(root) {
        var raw = root.getAttribute('data-swiper-config');
        if (!raw) {
            return null;
        }
        try {
            var parsed = JSON.parse(raw);
            return parsed && typeof parsed === 'object' ? parsed : null;
        } catch (e) {
            return null;
        }
    }

    /**
     * Clamp whole-number slidesPerView values to the real slide count, at the
     * root and inside every breakpoint. Fractional values (1.2, 'auto') are
     * deliberate peek layouts, not counts, and are left alone.
     */
    function capSlidesPerView(options, slideCount) {
        if (!slideCount || slideCount < 1) {
            return options;
        }
        var clamp = function (value) {
            return typeof value === 'number' && value % 1 === 0
                ? Math.min(value, slideCount)
                : value;
        };
        options.slidesPerView = clamp(options.slidesPerView);
        if (options.breakpoints) {
            Object.keys(options.breakpoints).forEach(function (bp) {
                var settings = options.breakpoints[bp];
                if (settings && typeof settings === 'object') {
                    settings.slidesPerView = clamp(settings.slidesPerView);
                }
            });
        }
        return options;
    }

    /**
     * Reproduces the two nav-visibility rules found inline in the blocks.
     * "slides" reads swiper.params.slidesPerView AFTER Swiper has applied the
     * active breakpoint, so it matches both the integer (1/2/3) comparison in
     * academic-credentials-26 and the fractional (1.2/2.2/3.2/4.2) one in
     * programs-related-26.
     */
    function applyNavVisibility(root, swiper, mode, slideCount) {
        var nav = ownQuery(root, NAV_CONTAINER_SELECTOR);
        var targets = nav
            ? [nav]
            : [ownQuery(root, NEXT_SELECTOR), ownQuery(root, PREV_SELECTOR)];

        var hide = false;
        if (mode === 'slides') {
            var perView = swiper.params.slidesPerView;
            hide = typeof perView === 'number' && perView >= slideCount;
        } else if (mode === 'desktop') {
            hide = window.innerWidth < DESKTOP_MIN_WIDTH || slideCount <= 3;
        }

        targets.forEach(function (el) {
            if (!el) {
                return;
            }
            el.style.display = hide ? 'none' : '';
            el.classList.toggle('navigation-hidden', hide);
        });
    }

    /**
     * The editor replaces a block's preview markup wholesale on every field edit,
     * and removes it on delete. Without this, each render leaves a live Swiper
     * bound to a detached element plus a stale registry entry. Swiper's own
     * destroy() detaches its listeners (swiper-bundle.js:4304-4314).
     */
    function pruneDetached() {
        Object.keys(window.coeSwiper.instances).forEach(function (id) {
            var instance = window.coeSwiper.instances[id];
            if (!instance || instance.destroyed) {
                delete window.coeSwiper.instances[id];
                return;
            }
            if (instance.el && !instance.el.isConnected) {
                // Clear the theme's own guard flag on the owning wrapper. Swiper's
                // destroy() does not touch it (swiper-bundle.js:4304-4347), and a
                // node that is removed and later reinserted would otherwise carry
                // data-swiper-initialized="true" with no live instance behind it —
                // permanently dead.
                var owner = instance.el.closest('[data-swiper-config]') || instance.el;
                if (owner && owner.dataset) {
                    delete owner.dataset.swiperInitialized;
                }
                try {
                    instance.destroy(true, false);
                } catch (e) {
                    // A half-torn-down instance is still worth dropping.
                }
                delete window.coeSwiper.instances[id];
            }
        });
    }

    function initOne(root) {
        if (root.dataset.swiperInitialized === 'true') {
            return null;
        }
        if (typeof window.Swiper === 'undefined') {
            return null;
        }

        // Defensive: check the root before descending. `statistics` is the only
        // block that ever put 'swiper' on its wrapper (statistics.php:57), and
        // Task 7 Step 2a moves it onto a proper inner container — because Swiper
        // requires .swiper-wrapper to be a DIRECT child (elementChildren() at
        // swiper-bundle.js:118-124, used by the wrapper lookup at :4207), which a
        // wrapper-level .swiper cannot guarantee. This branch stays as a cheap
        // guard against the pattern recurring.
        var container = root.matches('.swiper') ? root : ownQuery(root, '.swiper');
        var options = parseConfig(root);
        if (!container || !options) {
            return null;
        }

        root.dataset.swiperInitialized = 'true';

        var extras = options.coe || {};
        delete options.coe;

        if (extras.capSlidesPerView) {
            capSlidesPerView(options, extras.slideCount);
        }

        var next = ownQuery(root, NEXT_SELECTOR);
        var prev = ownQuery(root, PREV_SELECTOR);
        var pagination = ownQuery(root, PAGINATION_SELECTOR);

        if (next && prev) {
            options.navigation = Object.assign({}, options.navigation, {
                nextEl: next,
                prevEl: prev,
            });
        }
        if (pagination) {
            options.pagination = Object.assign({ clickable: true }, options.pagination, {
                el: pagination,
            });
        }

        var swiper = new window.Swiper(container, options);

        if (extras.navVisibility) {
            var sync = function () {
                applyNavVisibility(root, swiper, extras.navVisibility, extras.slideCount);
            };
            // Swiper already listens to window resize internally and emits its own
            // `resize` event (swiper-bundle.js:2228), so a second window listener
            // here would be redundant AND would leak on every editor re-render —
            // its closure keeps the detached preview alive. Use Swiper's events only.
            swiper.on('resize', sync);
            swiper.on('breakpoint', sync);
            // A plain call here is correct and sufficient. init() runs setBreakpoint()
            // BEFORE it emits 'afterInit' (swiper-bundle.js:4294), and setBreakpoint()
            // does `extend(swiper.params, breakpointParams)` at :474 — so by the time
            // the constructor returns, swiper.params.slidesPerView is already the
            // ACTIVE breakpoint's value, which is exactly what "slides" mode reads.
            // Do NOT try swiper.once('afterInit', ...): with the default init:true the
            // event fires synchronously inside `new Swiper(...)`, before any handler
            // attached on the next line could exist.
            sync();
        }

        // image-slider-with-links needed this after init; harmless everywhere else.
        swiper.update();

        var blockId = root.getAttribute('data-block-id') || root.id || null;
        if (blockId) {
            // Destroy any predecessor under this key before overwriting it.
            var previous = window.coeSwiper.instances[blockId];
            if (previous && previous !== swiper && !previous.destroyed) {
                try {
                    previous.destroy(true, false);
                } catch (e) {}
            }
            window.coeSwiper.instances[blockId] = swiper;
        }

        root.dispatchEvent(new CustomEvent('coe:swiper:init', {
            bubbles: true,
            detail: { swiper: swiper, root: root, blockId: blockId },
        }));

        return swiper;
    }

    /**
     * querySelectorAll() excludes the root element, so a scope that IS a wrapper
     * (an ACF preview replacement, for instance) has to be checked explicitly.
     */
    function init(scope) {
        var context = scope || document;
        if (context.nodeType === 1 && context.matches('[data-swiper-config]')) {
            initOne(context);
        }
        var roots = context.querySelectorAll('[data-swiper-config]');
        for (var i = 0; i < roots.length; i++) {
            initOne(roots[i]);
        }
    }

    /**
     * The editor replaces preview markup whenever a block is inserted or its
     * fields change, so a single DOM-ready scan is not enough. ACF's own
     * `render_block_preview` action fires in the PARENT document while the
     * markup lands in this iframe, so it cannot be subscribed to from here —
     * observe the DOM instead. Inert on the front end.
     *
     * IMPORTANT: observe documentElement, not body. On React 18 the canvas
     * iframe is built from a blob whose markup is literally
     * `<body><script>document.currentScript.parentElement.remove()</script></body>`
     * (wp-includes/js/dist/block-editor.js, getIframeSrc) — the body deletes
     * itself, and React mounts the real one later. Our script is injected into
     * that iframe's <head>, so at DOMContentLoaded `document.body` is null and
     * observe(document.body) would throw. documentElement always exists and its
     * subtree covers the body arriving.
     */
    function watchEditorPreviews() {
        var isEditor = window.frameElement !== null
            || !!document.querySelector('.block-editor-block-list__layout')
            || (document.body && document.body.classList.contains('editor-styles-wrapper'));
        if (!isEditor || typeof MutationObserver === 'undefined') {
            return;
        }
        var pending = null;
        var observer = new MutationObserver(function (records) {
            var changed = records.some(function (r) {
                return r.addedNodes.length > 0 || r.removedNodes.length > 0;
            });
            if (!changed) {
                return;
            }
            window.clearTimeout(pending);
            pending = window.setTimeout(function () {
                pruneDetached();
                init();
            }, 150);
        });
        observer.observe(document.documentElement, { childList: true, subtree: true });
    }

    window.coeSwiper.init = init;
    window.coeSwiper.initOne = initOne;

    function start() {
        init();
        watchEditorPreviews();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start);
    } else {
        start();
    }
})();
